/**
 * SearchableMultiSelect - pick several values from one searchable dropdown,
 * with the picks shown underneath as dismissible pills.
 *
 * Atomic Design role: Molecule. Composes the SearchableSelect molecule (the
 * type-to-filter dropdown) with the Chip atom (the pills), and owns nothing
 * but the derived option lists — the selected values live with the caller.
 *
 * This replaces the pattern of rendering every option as a checkbox in a
 * scrolling box. That is fine for four fixed choices and wrong for a list
 * that grows: with fifty companies on the account, the Admin user form asked
 * an admin to scroll a 160px window hunting for a name they could have typed
 * three letters of. The dropdown searches; the pills answer "who did I
 * already add?" without scrolling back through unchecked boxes.
 *
 * Options already picked are removed from the dropdown, so the same value
 * cannot be added twice and the list gets shorter as it gets used.
 *
 * @example
 * <SearchableMultiSelect
 *	   id="additional-companies"
 *	   options={companies.map((c) => ({ value: String(c.id), label: c.name }))}
 *	   selectedValues={form.additional_company_ids}
 *	   onChange={(ids) => setForm((p) => ({ ...p, additional_company_ids: ids }))}
 *	   placeholder="Search companies..."
 * />
 */
import React, { useCallback, useMemo } from 'react';
import { SearchableSelect, SearchableSelectOption } from '../SearchableSelect';
import { Chip } from '../../atoms';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface SearchableMultiSelectProps {
	/** Unique id for the search input. */
	id: string;
	/** Every selectable option. SearchableSelect dedupes and alphabetizes them. */
	options: SearchableSelectOption[];
	/** Currently selected values, in the order they were added. */
	selectedValues: string[];
	/** Called with the next selection whenever one is added or removed. */
	onChange: (next: string[]) => void;
	/** Placeholder for the search input. */
	placeholder?: string;
	/** Muted line shown under the picker while nothing is selected. */
	emptyText?: string;
	/** Replaces the picker once every option has been added. */
	allSelectedText?: string;
	/** Disables the picker; existing pills stay removable. */
	disabled?: boolean;
	/** Error state — passed through to the input's border. */
	hasError?: boolean;
	/** Extra classes merged onto the root. */
	className?: string;
	/** Overrides the molecule's data-ref so a composing component keeps its identity. */
	dataRef?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const SearchableMultiSelect: React.FC<SearchableMultiSelectProps> = ({
	id,
	options,
	selectedValues,
	onChange,
	placeholder = 'Search...',
	emptyText,
	allSelectedText = 'Everything on the list has been added.',
	disabled = false,
	hasError = false,
	className = '',
	dataRef = 'molecule-searchable-multi-select',
}) => {
	/* Label lookup built from the FULL option list, not the filtered one — the
	   pills need the labels of values that are no longer offered in the
	   dropdown, which is every value currently selected. */
	const labelByValue = useMemo(() => {
		const map = new Map<string, string>();
		for (const opt of options) map.set(opt.value, opt.label);
		return map;
	}, [options]);

	/* What's left to pick. Removing the selected values here is what stops a
	   value being added twice and shrinks the list as the form is filled in. */
	const remainingOptions = useMemo(() => {
		const chosen = new Set(selectedValues);
		return options.filter((opt) => !chosen.has(opt.value));
	}, [options, selectedValues]);

	/* SearchableSelect is a single-value control, so it's driven with a
	   permanently empty value: every pick is an "add" that lands in the pill
	   list and clears the input for the next one. */
	const handleAdd = useCallback((value: string) => {
		if (!value || selectedValues.includes(value)) return;
		onChange([...selectedValues, value]);
	}, [selectedValues, onChange]);

	const handleRemove = useCallback((value: string) => {
		onChange(selectedValues.filter((v) => v !== value));
	}, [selectedValues, onChange]);

	return (
		<div className={`searchable-multi-select ${className}`} data-ref={dataRef}>
			{/* Picker — or the "nothing left" line once it would be empty. An
			    empty dropdown that still opens reads like a broken control. */}
			{remainingOptions.length > 0 ? (
				<SearchableSelect id={id} options={remainingOptions} value="" onChange={handleAdd} placeholder={placeholder} disabled={disabled} hasError={hasError} />
			) : (
				<p className="text-12px font-sans text-brand-text-subtle italic">{allSelectedText}</p>
			)}

			{/* Pill container — the answer to "what have I added so far?" */}
			{selectedValues.length > 0 ? (
				<div className="searchable-multi-select__pills flex flex-wrap gap-8px mt-8px">
					{selectedValues.map((value) => (
						<Chip key={value} label={labelByValue.get(value) ?? value} onRemove={() => handleRemove(value)} dataRef="molecule-searchable-multi-select-pill" />
					))}
				</div>
			) : (
				emptyText && <p className="mt-8px text-12px font-sans text-brand-text-subtle italic">{emptyText}</p>
			)}
		</div>
	);
};

export default SearchableMultiSelect;
